import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaCheck,
  FaArrowUpRightFromSquare,
} from "react-icons/fa6";
import PageHero from "../components/PageHero";

export default function Facilitators() {
  useEffect(() => {
    document.title = 'Facilitators — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);
  return (
    <div className="w-full">
      <PageHero
        eyebrow="Facilitators"
        title={
          <>
            Experienced educators and accreditation experts —{" "}
            <span className="italic text-brand-deep">behind every workshop.</span>
          </>
        }
        lead="Our facilitators are practicing educators and accreditation specialists. They bring backgrounds in program evaluation, accreditation work, and research — though they do not officially represent any accrediting body."
      />

      <section className="bg-bone">
        <div className="container-page py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="eyebrow eyebrow-bar">Who facilitates</div>
            <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish text-ink">
              Practitioners who teach what they actively practice.
            </h2>
            <p className="mt-5 text-slate2 leading-relaxed text-lg">
              Every workshop, course, and engagement is led by someone with hands-on
              experience in the work — not just credentials about it. That experience
              shapes how sessions are structured, what examples are used, and how
              questions get answered in real time.
            </p>
            <p className="mt-4 text-slate2 leading-relaxed text-lg">
              We deliberately keep our facilitator network small so we can invest in
              the quality of every session.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-cream p-8 border border-ink/10">
              <div className="eyebrow">Disclosure</div>
              <p className="mt-3 text-ink leading-relaxed">
                While our facilitators may have experience with accrediting bodies or
                program evaluation organizations, their work with Learning Outcomes
                Academy is <span className="font-semibold">independent</span> and does
                not represent those organizations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured facilitator: Dr. Q */}
      <section id="dr-q" className="bg-cream scroll-mt-24">
        <div className="container-page py-16 lg:py-24">
          <div className="eyebrow eyebrow-bar">Featured facilitator</div>
          <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish text-ink max-w-3xl">
            Qusay H. Mahmoud, Ph.D., P.Eng.
          </h2>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            {/* Sidebar: photo + at a glance */}
            <aside className="lg:col-span-3">
              <div className="max-w-[260px]">
                <img
                  src="/courses/ai-for-parents/dr-q-circuit.jpg"
                  alt="Qusay H. Mahmoud (Dr. Q)"
                  className="block w-[calc(100%-8px)] mx-auto rounded-2xl object-cover border border-ink/10 shadow-soft"
                />
                <div className="mt-4 rounded-2xl bg-white p-7 border border-ink/10 shadow-soft">
                <div className="eyebrow">At a glance</div>
                <dl className="mt-6 space-y-5 text-sm">
                  <div>
                    <dt className="text-ink/55 text-[11px] tracking-[0.14em] uppercase">
                      Current role
                    </dt>
                    <dd className="mt-1 text-ink font-medium leading-snug">
                      Professor of Software Engineering, Ontario Tech University
                    </dd>
                  </div>
                  <div>
                    <dt className="text-ink/55 text-[11px] tracking-[0.14em] uppercase">
                      Research funding
                    </dt>
                    <dd className="mt-1 text-ink font-medium">$2M+</dd>
                  </div>
                  <div>
                    <dt className="text-ink/55 text-[11px] tracking-[0.14em] uppercase">
                      Taught &amp; researched in
                    </dt>
                    <dd className="mt-1 text-ink font-medium leading-snug">
                      Canada, United States, UAE, Indonesia, Saudi Arabia
                    </dd>
                  </div>
                  <div>
                    <dt className="text-ink/55 text-[11px] tracking-[0.14em] uppercase">
                      Founder
                    </dt>
                    <dd className="mt-1 text-ink font-medium">
                      Learning Outcomes Academy
                    </dd>
                  </div>
                  <div>
                    <dt className="text-ink/55 text-[11px] tracking-[0.14em] uppercase">
                      Elsewhere
                    </dt>
                    <dd className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <a
                        href="https://drqwrites.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-brand-deep font-medium hover:underline"
                      >
                        DrQwrites
                        <FaArrowUpRightFromSquare className="text-[10px]" />
                      </a>
                      <span aria-hidden="true" className="text-ink/30">·</span>
                      <a
                        href="https://www.linkedin.com/in/qmahmoud"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-brand-deep font-medium hover:underline"
                      >
                        LinkedIn
                        <FaArrowUpRightFromSquare className="text-[10px]" />
                      </a>
                    </dd>
                  </div>
                </dl>
                </div>
              </div>
            </aside>

            {/* Bio prose */}
            <div className="lg:col-span-9 space-y-5 text-slate2 leading-relaxed text-lg">
              <p>
                <span className="font-semibold text-ink">
                  Qusay H. Mahmoud
                </span>{" "}
                — known as{" "}
                <span className="font-semibold text-ink">Dr. Q</span> — is a Professor
                of Software Engineering with over two decades of experience in academia
                and industry. His work focuses on intelligent software systems, AI
                integration, and engineering education, with a strong emphasis on
                real-world impact.
              </p>
              <p>
                Before joining Ontario Tech University, he spent a decade at the
                University of Guelph, where he served as Professor of Computer Science
                and Director of the BlackBerry Centre for Mobile Education &amp;
                Research. He brings a global perspective to his work, having taught,
                conducted research, and delivered workshops across Canada, the United
                States, the UAE, Indonesia, and Saudi Arabia.
              </p>
              <p>
                Dr. Q has held several academic leadership roles, including Associate
                Dean and Founding Chair, and has led major initiatives in experiential
                learning, including engineering co-op and internship programs,
                innovation and entrepreneurship, and STEM outreach that engages
                students, families, and communities. He has also played a central role
                in the design, development, and external review of academic programs,
                contributing to curriculum innovation and quality assurance across
                institutions.
              </p>
              <p>
                His research program — supported by over $2 million in funding —
                focuses on intelligent systems and innovations in engineering education,
                with an emphasis on training students to solve real-world problems.
              </p>
              <p>
                As the founder of Learning Outcomes Academy and the voice behind{" "}
                <a
                  href="https://drqwrites.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-deep hover:underline font-medium"
                >
                  DrQwrites
                </a>
                , he is committed to making complex technologies, especially generative
                AI, accessible and practical. Through his courses and writing, he helps
                learners, parents, and educators understand what AI is, what it is not,
                and how to use it thoughtfully and with confidence.
              </p>

              <div className="pt-2">
                <Link
                  to="/courses/ai-for-parents"
                  className="inline-flex items-center gap-2 text-brand-deep font-medium hover:underline"
                >
                  See Dr. Q&apos;s course: Generative AI for Parents{" "}
                  <FaArrowRight className="text-xs" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Join us */}
      <section className="bg-ink text-bone">
        <div className="container-page py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow !text-brand-sage">
              <span className="inline-block h-px w-8 bg-brand-sage mr-2 align-middle" />
              Join the team
            </div>
            <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish">
              We're seeking facilitators to lead new training and accreditation workshops.
            </h2>
            <p className="mt-5 text-bone/75 leading-relaxed text-lg">
              If you have practical experience in accreditation, program evaluation,
              research, or education — and you love teaching — we'd like to hear from
              you.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 bg-bone text-ink font-medium px-6 py-3 rounded-full hover:bg-cream transition-colors"
            >
              Apply to facilitate <FaArrowRight className="text-xs" />
            </Link>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-bone/15 p-8">
              <div className="eyebrow !text-brand-sage">
                <span className="inline-block h-px w-8 bg-brand-sage mr-2 align-middle" />
                Ideal candidate
              </div>
              <ul className="mt-6 space-y-4">
                {[
                  "Relevant accreditation, program evaluation, or research experience",
                  "Effective communication and interpersonal skills",
                  "Experience teaching or presenting workshops to mixed audiences",
                  "Comfort designing applied, practical sessions — not lectures",
                  "Commitment to high-quality preparation and follow-through",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-sage text-ink">
                      <FaCheck className="text-[10px]" />
                    </span>
                    <span className="text-bone/85 leading-relaxed">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
